import { isEmpty, isNil, isString } from 'lodash';
import React, { ReactNode, useContext, useMemo } from 'react';
import { IconButtonV2 } from '@shared/components/MBButton';
import { View } from '@shared/components/View';
import { MBBlockLabel } from '@shared/components/blocks/utils/label';
import { MBHover } from '@shared/components/hover';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { AnchoredTooltip } from '@shared/components/tooltip';
import { IsCardBeforePaidCutoffHandler } from '@shared/data/billing/hooks/useIsCardBeforePaidCutoff';
import {
  getComponentWithOverrides,
  isComponentHidden as getIsComponentHidden,
  isComponentExcludedFromCollectionView,
} from '@shared/data/cards/utils';
import { Entity } from '@shared/data/entity/Entity';
import { TagField } from '@shared/data/entity/fields/TagField';
import { useUpdateWorkflowEntity } from '@shared/scenes/entity/edit/hooks/useUpdateWorkflowEntity';
import {
  ChecklistTagComponent,
  getComponentIcon,
  getComponentLabel,
  isChecklistTagComponent,
  isInputComponent,
  isManagingInputComponent,
} from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { CollectionViewAnalytics } from '../../collectionView/CollectionViewAnalytics';
import {
  CollectionViewCard,
  CollectionViewCardStyles,
} from '../../collectionView/components/CollectionViewCard';
import {
  CardCollectionViewField,
  FieldStyles,
} from '../../collectionView/components/fields/CardCollectionViewField';
import { OpenEntityHandler } from '../../collectionView/hooks/useCollectionViewData';
import { headerFieldStyles, wideCardFieldStyles } from './listViewCardStyles';
import CardStyles from './Card.module.css';

export interface CardProps {
  viewTemplate: API.ViewTemplateFragment;
  workflowTemplate: API.WorkflowTemplateFragment;
  entity: Entity;
  openEntity?: OpenEntityHandler;
  getIsCardBeforePaidCutoff: IsCardBeforePaidCutoffHandler;
  canUpdateCard?: boolean;
  analytics?: CollectionViewAnalytics;
  isRedacted?: boolean;
  hasOverflowMenu?: boolean;
  cardStyles?: CollectionViewCardStyles;
  testID?: string;
}

export function Card({
  viewTemplate,
  workflowTemplate,
  openEntity,
  getIsCardBeforePaidCutoff,
  entity,
  analytics,
  canUpdateCard = false,
  isRedacted = false,
  hasOverflowMenu = true,
  cardStyles,
  testID,
}: CardProps) {
  const [firstComponent, otherComponents] = useVisibleComponents({
    workflowTemplate,
    viewTemplate,
    entity,
  });

  const checklistComponent = getChecklistComponent({ viewTemplate, workflowTemplate });

  return (
    <View flex testID={testID} className={CardStyles.card}>
      <CollectionViewCard
        workflowTemplate={workflowTemplate}
        entity={entity}
        openEntity={openEntity}
        getIsCardBeforePaidCutoff={getIsCardBeforePaidCutoff}
        hasOverflowMenu={hasOverflowMenu}
        cardStyles={cardStyles}
      >
        <RedactedWrapper isRedacted={isRedacted}>
          <CheckListWrapper
            entity={entity}
            checklistComponent={checklistComponent}
            viewTemplateId={viewTemplate.id}
          >
            {!isNil(firstComponent) && (
              <CardCollectionViewComponent
                component={firstComponent}
                entity={entity}
                workflowTemplateId={workflowTemplate.id}
                fieldStyles={headerFieldStyles}
                showLabel={isRedacted ? false : !viewTemplate.defaultHiddenLabels}
                analytics={analytics}
                canUpdateCard={canUpdateCard}
                isRedacted={isRedacted}
              />
            )}
            <View className={CardStyles.otherComponents}>
              {!isRedacted &&
                otherComponents.map(component => {
                  return (
                    <CardCollectionViewComponent
                      key={component.id}
                      component={component}
                      entity={entity}
                      workflowTemplateId={workflowTemplate.id}
                      fieldStyles={wideCardFieldStyles}
                      showLabel={!viewTemplate.defaultHiddenLabels}
                      analytics={analytics}
                      canUpdateCard={canUpdateCard}
                      isRedacted={false}
                    />
                  );
                })}
              {isRedacted && <View className={CardStyles.redactedPlaceholder} />}
            </View>
          </CheckListWrapper>
        </RedactedWrapper>
      </CollectionViewCard>
    </View>
  );
}

function RedactedWrapper({
  children,
  isRedacted,
}: {
  children: React.ReactNode;
  isRedacted: boolean;
}) {
  return <View className={isRedacted && CardStyles.redacted}>{children}</View>;
}

function CheckListWrapper({
  children,
  entity,
  checklistComponent,
  viewTemplateId,
}: {
  children: ReactNode;
  entity: Entity;
  checklistComponent?: ChecklistTagComponent;
  viewTemplateId: string;
}) {
  if (isNil(checklistComponent)) {
    return <>{children}</>;
  } else {
    return (
      <View row className={CardStyles.checklistWrapper}>
        <ChecklistButton
          entity={entity}
          checklistComponent={checklistComponent}
          viewTemplateId={viewTemplateId}
        />
        <View fill>{children}</View>
      </View>
    );
  }
}

function useVisibleComponents({
  workflowTemplate,
  viewTemplate,
  entity,
}: {
  workflowTemplate: API.WorkflowTemplateFragment;
  viewTemplate: API.ViewTemplateFragment;
  entity: Entity;
}): [API.AnyComponent, API.AnyComponent[]] {
  return useMemo(() => {
    const filtered = workflowTemplate.components
      .filter(component => {
        const viewOptions = viewTemplate.componentsViewOptions?.find(
          options => options.componentId === component.id,
        );
        const field = entity.fields.get(component.id);
        const isHiddenWhenEmpty =
          isInputComponent(component) || isManagingInputComponent(component);
        const isFieldEmpty = isNil(field) || field.isEmpty;
        const isComponentHidden = getIsComponentHidden(viewTemplate, viewOptions, component);
        return (
          !isComponentHidden &&
          (!isFieldEmpty || !isHiddenWhenEmpty) &&
          !isComponentExcludedFromCollectionView(component)
        );
      })
      .map(component => getComponentWithOverrides(component, viewTemplate));
    const [firstComponent, ...otherComponents] = filtered;
    return [firstComponent, otherComponents];
  }, [workflowTemplate, viewTemplate, entity]);
}

function getChecklistComponent({
  viewTemplate,
  workflowTemplate,
}: {
  viewTemplate: API.ViewTemplateFragment;
  workflowTemplate: API.WorkflowTemplateFragment;
}): ChecklistTagComponent | undefined {
  if (viewTemplate.subtype !== 'LIST') {
    return undefined;
  }
  const checklistComponent = workflowTemplate.components
    .filter(isChecklistTagComponent)
    .find(component => component.id === viewTemplate.checklistTagComponentId);
  return checklistComponent;
}

interface ChecklistButtonProps {
  entity: Entity;
  checklistComponent: ChecklistTagComponent;
  viewTemplateId: string;
}

function ChecklistButton({ entity, checklistComponent, viewTemplateId }: ChecklistButtonProps) {
  const { updateWorkflowEntity, loading } = useUpdateWorkflowEntity();
  const [isHovered, onHoverStart, onHoverEnd] = useBoolean(false);
  const colors = useContext(ColorThemeContext);
  const field = getTagField(entity, checklistComponent);
  const { checkedOption, uncheckedOption } = checklistComponent.checklistOptions;
  const isCompleted = field?.tagIds?.includes(checkedOption);
  // optimistically invert completed state while request is in flight
  const completed = loading ? !isCompleted : isCompleted;
  const color = completed || isHovered ? colors.accent : colors.accentInactive;
  const iconName = completed ? 'task-filled' : 'task';
  const label = getComponentLabel(checklistComponent);
  const opacity = completed ? (isHovered ? 0.9 : 1) : isHovered ? 1 : 0.7;

  const onPress = () => {
    updateWorkflowEntity(
      entity.id,
      { [checklistComponent.id]: isCompleted ? [uncheckedOption] : [checkedOption] },
      viewTemplateId,
    );
  };

  return (
    <View center paddingLeft={1} paddingTop={0.5} className={CardStyles.checklistButton}>
      <AnchoredTooltip tooltip={i18n('collectionView.listView.checkList.toggleTooltip', { label })}>
        <MBHover onHoverStart={onHoverStart} onHoverEnd={onHoverEnd} style={{ opacity }}>
          <IconButtonV2 size={grid(4.5)} name={iconName} color={color} onPress={onPress} />
        </MBHover>
      </AnchoredTooltip>
    </View>
  );
}

function getTagField(entity: Entity, checklistComponent: ChecklistTagComponent): TagField | null {
  const field = entity.allFields.get(checklistComponent.id);
  if (field instanceof TagField) {
    return field;
  } else {
    return null;
  }
}

interface CardCollectionViewComponentProps {
  component: API.AnyComponent;
  entity: Entity;
  workflowTemplateId: string;
  fieldStyles: FieldStyles;
  showLabel: boolean;
  analytics?: CollectionViewAnalytics;
  canUpdateCard: boolean;
  isRedacted?: boolean;
  isCollapsed?: boolean;
}

function CardCollectionViewComponent({
  component,
  entity,
  workflowTemplateId,
  fieldStyles,
  showLabel,
  analytics,
  canUpdateCard,
  isRedacted = false,
  isCollapsed = false,
}: CardCollectionViewComponentProps) {
  const colors = useContext(ColorThemeContext);
  const field = entity.fields.get(component.id);
  const componentIconName = getComponentIcon(component);
  const componentLabel = getComponentLabel(component);

  return (
    <View
      className={[
        CardStyles.component,
        isRedacted && CardStyles.redacted,
        showLabel && CardStyles.showLabel,
      ]}
    >
      {!isEmpty(componentIconName) && (
        <View paddingTop={0.3}>
          <MBIconV2 name={componentIconName as MBIconV2Name} size={grid(4)} color={colors.accent} />
        </View>
      )}
      <View paddingHorizontal={1} fill>
        {showLabel && isString(componentLabel) && !isEmpty(componentLabel) && (
          <MBBlockLabel label={componentLabel} />
        )}
        <CardCollectionViewField
          field={field}
          workflowTemplateId={workflowTemplateId}
          entityId={entity.id}
          component={component}
          fieldStyles={fieldStyles}
          isCollapsed={isCollapsed}
          showLabel={showLabel}
          canUpdateCard={canUpdateCard}
          analytics={analytics}
        />
      </View>
    </View>
  );
}
